import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Chip } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import RightDrawer from "../../../components/RightDrawer";
import { httprequest } from "../../../data/api";
import luluAvatar from "../../../assets/images/lulurobot.png";

const LULU_ENDPOINT = "/api/v1/lesson-outlines/ai/generate";

const INITIAL_BOT_MESSAGE = { type: "bot", text: "Hi! I'm Lulu. Select a class & subject below, then tell me the topic and I'll help generate your lesson outline." };

const renderMarkdown = (text) => {
  const lines = text.split("\n");
  const result = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith("# ")) {
      result.push(
        <p key={i} style={{ margin: "10px 0 2px", fontSize: 13, fontWeight: 700, color: "#101828" }}>
          {line.slice(2)}
        </p>
      );
    } else if (line.startsWith("## ")) {
      result.push(
        <p key={i} style={{ margin: "10px 0 2px", fontSize: 12, fontWeight: 700, color: "#0065FF", textTransform: "uppercase", letterSpacing: "0.4px" }}>
          {line.slice(3)}
        </p>
      );
    } else if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(<li key={i} style={{ marginBottom: 2 }}>{lines[i].replace(/^\d+\.\s/, "")}</li>);
        i++;
      }
      result.push(<ol key={`ol-${i}`} style={{ margin: "4px 0 4px 18px", padding: 0 }}>{items}</ol>);
      continue;
    } else if (line.startsWith("- ")) {
      const items = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        items.push(<li key={i} style={{ marginBottom: 2 }}>{lines[i].slice(2)}</li>);
        i++;
      }
      result.push(<ul key={`ul-${i}`} style={{ margin: "4px 0 4px 18px", padding: 0 }}>{items}</ul>);
      continue;
    } else if (line.trim() !== "") {
      result.push(
        <p key={i} style={{ margin: "4px 0" }}>{line}</p>
      );
    }

    i++;
  }

  return result;
};

const LuluDrawer = ({ open, onClose, lessonNotes, onApplyContent }) => {
  const [selectedNote, setSelectedNote] = useState(null);
  const [messages, setMessages] = useState([INITIAL_BOT_MESSAGE]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [originalTopic, setOriginalTopic] = useState("");
  const [input, setInput] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    if (!open) {
      setSelectedNote(null);
      setMessages([INITIAL_BOT_MESSAGE]);
      setConversationHistory([]);
      setOriginalTopic("");
      setInput("");
    }
  }, [open]);

  const generateContent = (classId, subjectId, topic, history) => {
    setIsGenerating(true);
    httprequest
      .post(LULU_ENDPOINT, {
        class_id: classId,
        subject_id: subjectId,
        topic,
        context: "",
        instruction: "",
        messages: history,
        attachments: []
      })
      .then((res) => {
        const data = res?.data?.data;
        const prefill = data?.prefill;
        const botText = prefill?.lesson_outline || "Here is the generated lesson outline.";
        setConversationHistory((prev) => [...prev, { role: "assistant", content: botText }]);
        setMessages((prev) => [...prev, { type: "bot", text: botText, content: prefill }]);
        setIsGenerating(false);
      })
      .catch(() => {
        setMessages((prev) => [...prev, { type: "bot", text: "Sorry, I couldn't generate content right now. Please try again." }]);
        setIsGenerating(false);
      });
  };

  const handleSelectNote = (note) => {
    setSelectedNote(note);
    setConversationHistory([]);
    setOriginalTopic("");
    setMessages((prev) => [
      ...prev,
      { type: "bot", text: `Great! I'll help with ${note.class?.name} — ${note.subject?.name}. What topic would you like me to create a lesson outline for?` }
    ]);
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text || isGenerating) {
      return;
    }

    setMessages((prev) => [...prev, { type: "user", text }]);
    setInput("");

    if (!selectedNote) {
      setMessages((prev) => [...prev, { type: "bot", text: "Please select a class & subject first." }]);

      return;
    }

    const topic = originalTopic || text;
    if (!originalTopic) setOriginalTopic(text);

    const updatedHistory = [...conversationHistory, { role: "user", content: text }];
    setConversationHistory(updatedHistory);

    generateContent(selectedNote.class?.id, selectedNote.subject?.id, topic, updatedHistory);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleApply = (content) => {
    if (onApplyContent) {
      onApplyContent({ ...content, lessonNote: selectedNote });
    }
    onClose();
  };

  const uniqueNotes = lessonNotes?.filter(
    (note, idx, arr) => arr.findIndex((n) => n.id === note.id) === idx
  ) || [];

  return (
    <RightDrawer open={open} onClose={onClose} title="Lulu AI" width={500}>
      <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 160px)" }}>
        {uniqueNotes.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <p style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 600, color: "#344054" }}>
              Select Class &amp; Subject
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {uniqueNotes.map((note) => (
                <Chip
                  key={note.id}
                  label={`${note.class?.name} | ${note.subject?.name}`}
                  onClick={() => handleSelectNote(note)}
                  variant={selectedNote?.id === note.id ? "filled" : "outlined"}
                  color={selectedNote?.id === note.id ? "primary" : "default"}
                  size="small"
                  style={{ cursor: "pointer", fontSize: 12 }}
                />
              ))}
            </div>
          </div>
        )}

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 12,
            paddingRight: 4
          }}
        >
          {messages.map((msg, i) => (
            msg.type === "bot" ? (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <img src={luluAvatar} alt="Lulu" style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0 }} />
                <div
                  style={{
                    background: "#F2F4F7",
                    borderRadius: "0 12px 12px 12px",
                    padding: "10px 14px",
                    maxWidth: "80%",
                    fontSize: 13,
                    color: "#344054",
                    lineHeight: 1.6
                  }}
                >
                  {msg.content ? renderMarkdown(msg.text) : msg.text}
                  {msg.content && (
                    <button
                      onClick={() => handleApply(msg.content)}
                      style={{
                        display: "block",
                        marginTop: 10,
                        background: "#0065FF",
                        color: "#fff",
                        border: "none",
                        borderRadius: 8,
                        padding: "6px 14px",
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer"
                      }}
                    >
                      Accept Response
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div key={i} style={{ display: "flex", justifyContent: "flex-end" }}>
                <div
                  style={{
                    background: "#0065FF",
                    color: "#fff",
                    borderRadius: "12px 12px 0 12px",
                    padding: "10px 14px",
                    maxWidth: "80%",
                    fontSize: 13,
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap"
                  }}
                >
                  {msg.text}
                </div>
              </div>
            )
          ))}
          {isGenerating && (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <img src={luluAvatar} alt="Lulu" style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0 }} />
              <div style={{ background: "#F2F4F7", borderRadius: "0 12px 12px 12px", padding: "10px 14px", fontSize: 13, color: "#667085" }}>
                Generating...
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        <div
          style={{
            marginTop: 12,
            display: "flex",
            alignItems: "flex-end",
            gap: 8,
            borderTop: "1px solid #EAECF0",
            paddingTop: 12
          }}
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your topic or message..."
            rows={3}
            style={{
              flex: 1,
              resize: "none",
              border: "1px solid #D0D5DD",
              borderRadius: 10,
              padding: "10px 14px",
              fontSize: 13,
              fontFamily: "inherit",
              color: "#344054",
              outline: "none",
              lineHeight: 1.5
            }}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isGenerating}
            style={{
              background: input.trim() && !isGenerating ? "#0065FF" : "#D0D5DD",
              border: "none",
              borderRadius: 10,
              width: 42,
              height: 42,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: input.trim() && !isGenerating ? "pointer" : "not-allowed",
              flexShrink: 0,
              transition: "background 0.2s"
            }}
          >
            <SendIcon style={{ color: "#fff", fontSize: 18 }} />
          </button>
        </div>
      </div>
    </RightDrawer>
  );
};

LuluDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  lessonNotes: PropTypes.array,
  onApplyContent: PropTypes.func
};

export default LuluDrawer;
